/**
 * Network calls for the document canvas, each logged with what support needs
 * (kind, counts, timings, error codes) and nothing a user typed.
 */
import {
  documentDraftsApi,
  panelPlansApi,
  type DocumentDraft,
  type DocumentSectionDraft,
  type DocumentSectionKind,
  type PanelPlan,
} from "../../services/api";
import { createLogger, errorFields } from "../../services/clientLog";
import type { CanvasSource } from "../canvas/canvasModel";

export const logDocument = createLogger("document_canvas");

export async function planSection(
  kind: DocumentSectionKind,
  question: string,
  source: CanvasSource,
): Promise<PanelPlan> {
  const started = performance.now();
  try {
    const plan = await panelPlansApi.plan({
      question,
      contextId: source.contextId,
      contextVersion: source.contextVersion || undefined,
    });
    logDocument("info", "section_planned", {
      kind,
      status: plan.status,
      questionLength: question.length,
      ms: Math.round(performance.now() - started),
    });
    return plan;
  } catch (error) {
    logDocument("warn", "section_plan_failed", { kind, ...errorFields(error) });
    throw error;
  }
}

export async function composeDocument(request: {
  title: string;
  subject: string;
  source: CanvasSource;
  sections: DocumentSectionDraft[];
}): Promise<DocumentDraft> {
  const started = performance.now();
  try {
    const draft = await documentDraftsApi.compose({
      title: request.title,
      subject: request.subject || undefined,
      contextId: request.source.contextId,
      contextVersion: request.source.contextVersion || undefined,
      sections: request.sections,
    });
    logDocument("info", "document_composed", {
      artifactId: draft.artifact.id,
      sections: request.sections.length,
      results: draft.results.length,
      ms: Math.round(performance.now() - started),
    });
    return draft;
  } catch (error) {
    logDocument("error", "document_compose_failed", {
      sections: request.sections.length,
      ...errorFields(error),
    });
    throw error;
  }
}
